<template>
  <div class="unsplash">
    <div class="input-wrapper">
      <form class="unsplash-search" @submit.prevent="search">
        <div>
          <input v-model="query" placeholder="Search for something..."/>
        </div>

        <div class="search-button">
          <button class="btn is-secondary">
            <Sprite name="search"/>
          </button>
        </div>
      </form>
    </div>

    <div v-if="!images.length && searching">
      <p>Sorry, no results found for that!</p>
    </div>

    <div v-else class="photo-results" ref="unsplashResults">
      <div
        v-for="img in images"
        :class="['unsplash-image', {'is-selected': isActive(img)}]"
        :style="`background-image: url('${img.urls.small}')`"
        @click="setImage(img)"
        :key="img.id">

        <img
          class="image-loading"
          :src="require('../assets/loading.svg')"
          v-if="fileUploading">

        <div class="unsplash-credit">
          <a :href="img.user.links.self" target="_blank">
            {{img.user.name}}
          </a>
          <br>
          <a href="https://unsplash.com/?utm_source=PageMakerPro&utm_medium=referral" target="_blank">
            on Unsplash
          </a>
        </div>
      </div>

      <div class="pagination-controls">
        <button
          @click.prevent="prevPage"
          :disabled="isFirstPage"
          class="btn prev-page is-secondary">
          <Sprite name="arrow"/>
        </button>

        <button
          @click.prevent="nextPage"
          class="btn is-secondary">
          <Sprite name="arrow"/>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Ajax from 'ajax/src/ajax'

import Sprite from '@/components/Sprite'

const CLIENT_ID = process.env.VUE_APP_UNSPLASH_TOKEN

export default {
  name: 'unsplash',

  data() {
    return {
      query: '',
      searching: false,
      selectedImageId: '',
      page: 1,
      images: []
    }
  },

  props: {
    fileUploading: Boolean
  },

  components: {
    Sprite
  },

  computed: {
    url() {
      const base = `https://api.unsplash.com`
      const clientIdParam = `client_id=${CLIENT_ID}`
      const pageParam = `page=${this.page}`
      const perPageParam = 'per_page=30'

      if (this.searching) {
        return `${base}/search/photos?query=${this.query}&${clientIdParam}&${pageParam}&${perPageParam}`
      } else {
        return `${base}/photos?${clientIdParam}&${pageParam}&${perPageParam}`
      }
    },

    isActive() {
      return (img) => {
        return img.id === this.selectedImageId
      }
    },

    isFirstPage() {
      return this.page === 1
    }
  },

  methods: {
    search() {
      this.searching = true
      this.page = 1
      this.getPhotos()
    },

    prevPage() {
      this.page--
      this.getPhotos()
    },

    nextPage() {
      this.page++
      this.getPhotos()
    },

    getPhotos() {
      Ajax.request({
        url: this.url,

        onSuccess: (xhr) => {
          let images = JSON.parse(xhr.responseText)

          if (this.searching) {
            this.images = images.results
          } else {
            this.images = images
          }

          this.scrollUp()
        },

        onError: (xhr) => {
          alert(xhr)
        }
      })
    },

    setImage(img) {
      const clientIdParam = `client_id=${CLIENT_ID}`
      let imageUrl = `${img.links.download}?${clientIdParam}&w=1200`
      this.selectedImageId = img.id
      this.$emit('setImage', imageUrl)
    },

    scrollUp() {
      this.$refs.unsplashResults.scrollTop = 0
    }
  },

  mounted() {
    this.getPhotos()
  }
}
</script>

<style lang="sass">
  @import '../stylesheets/base/variables'

  .unsplash
    width: 100%

    *
      width: 100%

    .input-wrapper
      background: transparent

  .photo-results
    display: flex
    flex-wrap: wrap
    margin: $gap-small 0 0
    max-height: 280px
    overflow: auto

  .unsplash-search
    display: flex
    align-items: center

    .search-button
      flex-basis: 50px
      margin-left: $gap-small

      .sprite
        width: 20px
        height: 16px

  .unsplash-image
    flex-basis: calc(#{percentage(1/2)} - 2px)
    height: 125px
    background-size: cover
    background-position: 50% 50%
    margin: 1px
    cursor: pointer
    display: inline-flex
    flex-direction: column
    justify-content: top
    align-items: center
    position: relative

    &:before
      content: 'Selected!'
      font-weight: bold
      text-transform: uppercase
      line-height: 125px
      position: absolute
      top: 0
      left: 0
      width: 100%
      height: 100%
      background: rgba(255, 255, 255, 0.75)
      opacity: 0
      transition: opacity .2s ease-in-out

    &.is-selected
      &:before
        opacity: 1

      .image-loading
        display: block
        opacity: 1

      &:hover
        .unsplash-credit
          opacity: 0

    &:hover
      .unsplash-credit
        opacity: 1

  .image-loading
    background: rgba(0, 0, 0, 0.25)
    position: absolute
    width: 50px
    height: 30px
    border-radius: 20px
    bottom: 10px
    left: 50%
    transform: translateX(-50%)
    padding: 0 2px
    display: none
    transition: opacity .3s .5s ease-in-out
    opacity: 0

  .unsplash-credit
    background: rgba(255, 255, 255, 0.75)
    padding: 3px
    opacity: 0
    transition: opacity .2s ease-in-out
    font-size: 8px
    width: 75%
    border-bottom-left-radius: 5px
    border-bottom-right-radius: 5px
    z-index: 1

    a
      color: #333
      font-weight: bold
      opacity: 0.5

  .pagination-controls
    position: absolute
    width: 100px
    bottom: 0
    left: 50%
    transform: translateX(-50%)
    border-top-left-radius: 5px
    border-top-right-radius: 5px
    display: flex
    justify-content: center
    background: rgba(255, 255, 255, 0.75)
    padding: 0
    z-index: 10

    .btn
      margin: 5px 10px
      max-width: 30px

      .sprite
        width: 10px
        height: 10px

  .prev-page
    .sprite
      transform: scaleX(-1)
</style>
